'use client'

import { useEffect, useState } from 'react';

interface ErrorProps {
  error?: Error & { digest?: string };
  reset: () => void;
}

// Simple useHasMounted hook to avoid external dependency issues
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return hasMounted;
}

// Simple warning triangle SVG icon
const AlertTriangleIcon = () => (
  <svg 
    className="h-12 w-12 text-red-500" 
    fill="none" 
    viewBox="0 0 24 24" 
    stroke="currentColor"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" 
    />
  </svg>
);

// Simple translation function with hardcoded fallbacks
function getTranslation(key: string): string {
  const translations: Record<string, string> = {
    'errors.unknown_error': 'An unknown error occurred',
    'errors.something_went_wrong': 'Something went wrong',
    'errors.unexpected_error_description': 'An unexpected error occurred. Please try again.',
    'errors.try_again': 'Try Again',
    'errors.go_to_homepage': 'Go to Homepage'
  };
  
  return translations[key] || key;
}

export default function Error({ error, reset }: ErrorProps) {
  const hasMounted = useHasMounted();
  
  const safeError = error || { 
    name: 'UnknownError', 
    message: getTranslation('errors.unknown_error'),
    stack: undefined
  } as Error;

  const displayTitle = getTranslation('errors.something_went_wrong');
  const displayDescription = getTranslation('errors.unexpected_error_description');

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center border">
        <div className="flex justify-center mb-4">
          <AlertTriangleIcon />
        </div>
        
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {displayTitle}
        </h1>
        
        <p className="text-muted-foreground mb-6">
          {displayDescription}
        </p>
        
        <div className="space-y-3">
          <button
            onClick={reset}
            className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 transition-colors"
          >
            {getTranslation('errors.try_again')}
          </button>
          
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-secondary text-secondary-foreground py-2 px-4 rounded-md hover:bg-secondary/80 transition-colors"
          >
            {getTranslation('errors.go_to_homepage')}
          </button>
        </div>
        
        {process.env.NODE_ENV === 'development' && hasMounted && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Error Details (Development)
            </summary>
            <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
              {safeError?.message || 'Unknown error'}
              {safeError?.stack && (
                <>
                  {'\n\nStack Trace:\n'}
                  {safeError.stack}
                </>
              )}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
} 
